/**
 * @fileoverview Conversation list component for the messages sidebar.
 * Displays searchable list of SMS conversation threads with selection state.
 */

import { useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ConversationListItem } from './ConversationListItem';
import type { ConversationThread } from '@/types/message';

/**
 * Props for ConversationList component.
 */
interface ConversationListProps {
  /** Array of conversation threads to display */
  threads: ConversationThread[];
  /** Currently selected phone number, or null */
  selectedPhone: string | null;
  /** Callback when a thread is selected */
  onSelectThread: (phoneNumber: string) => void;
  /** Loading state for skeleton display */
  isLoading: boolean;
}

/**
 * Renders a searchable, scrollable list of conversation threads.
 * Includes loading skeleton and empty states.
 */
export function ConversationList({
  threads,
  selectedPhone,
  onSelectThread,
  isLoading,
}: ConversationListProps) {
  const [search, setSearch] = useState('');

  const filteredThreads = threads.filter((thread) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      thread.phone_number.includes(search) ||
      thread.last_message_preview.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="flex flex-col h-full border-r border-border">
      {/* Search */}
      <div className="flex-shrink-0 bg-background p-4 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search conversations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Conversation List */}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="p-4 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-muted rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-1/2" />
                    <div className="h-3 bg-muted rounded w-3/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredThreads.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            {search ? 'No conversations match your search' : 'No conversations yet'}
          </div>
        ) : (
          filteredThreads.map((thread) => (
            <ConversationListItem
              key={thread.phone_number}
              thread={thread}
              isSelected={selectedPhone === thread.phone_number}
              onClick={() => onSelectThread(thread.phone_number)}
            />
          ))
        )}
      </ScrollArea>
    </div>
  );
}
